import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import {
  FormColumn,
  FormRowGroup,
} from 'modules/tickets/components/common/dialog/TicketFormUtils';
import {
  Title,
  SubTitle,
  DetailsBox,
  Row,
  Divider,
  RefundableRow,
} from './styles/WithdrawalStyles';
import { VoucherDetailsRows } from './WithdrawalDetailsShared';
import form from 'modules/ticketPublicPage/stores/vouchers/form';
import { PAYMENT_TYPES } from 'app/constants';
import facility from 'app/facility';

import { colors } from 'app/variables';
import InlineAlert from 'app/commonComponents/InlineAlert';
import TextField from 'app/commonComponents/TextField';

export const textFieldLabelStyle = {
  fontSize: '0.6rem',
  fontWeight: 'initial',
  color: colors.dustyGray,
};

const ReasonWrapper = styled(FormRowGroup)`
  textarea {
    text-align: left !important;

    &::placeholder {
      font-size: 0.9rem;
    }
  }
`;

const WithdrawalSummary = observer(() => {
  const { formValues, validateField, setFormValue, errors } = form;

  const {
    ref_num: refNum,
    buyer_name: buyerName,
    email,
    created,
    amount,
    amount_left: amountLeft,
    currency,
    claims,
    withdrawal_period_expired: withdrawalPeriodExpired,
    withdrawal_days_left: withdrawalDaysLeft,
    withdrawal_cancellation_requested: withdrawalCancellationRequested,
    payment,
    id,
  } = formValues.get('withdrawal_voucher_purchase') || {};

  const { paymentType } = payment || {};

  const hasClaims = claims.length > 0;

  const paymentTypeLabel =
    PAYMENT_TYPES[paymentType] || i18n.gettext('Unknown');
  const isRefundable =
    !withdrawalPeriodExpired && id && !withdrawalCancellationRequested;
  const formattedAmountLeft = facility.getFormattedPrice(amountLeft, currency);

  return (
    <div>
      <FormRowGroup style={{ width: '100%' }}>
        <FormColumn style={{ width: '100%' }}>
          <Title>{i18n.gettext('Cancel the contract')}</Title>
          {isRefundable ? (
            <SubTitle>
              {i18n.gettext(
                'You have 14 days to cancel your purchase. If vouchers have already been used, you may be required to reimburse the value.'
              )}
            </SubTitle>
          ) : null}
          <DetailsBox $border>
            <VoucherDetailsRows
              refNum={refNum}
              buyerName={buyerName}
              email={email}
              created={created}
              amount={amount}
              currency={currency}
              claims={claims}
            />

            {isRefundable ? (
              <Fragment>
                <Divider />
                <RefundableRow $color={colors.pacificBlue}>
                  <span>
                    {i18n.sprintf(
                      i18n.gettext('Refundable (within %s days)'),
                      withdrawalDaysLeft || 0
                    )}
                  </span>
                  <span style={{ fontWeight: 600 }}>{formattedAmountLeft}</span>
                </RefundableRow>
              </Fragment>
            ) : withdrawalCancellationRequested ? (
              <Fragment>
                <RefundableRow>
                  <span>{i18n.gettext('Refundable (already withdrawn)')}</span>
                  <span>{formattedAmountLeft}</span>
                </RefundableRow>
              </Fragment>
            ) : (
              <InlineAlert type="error" style={{ fontSize: '0.9rem' }}>
                {i18n.gettext(
                  'The 14-day cancellation period has expired. Unfortunately, you can no longer cancel your order.'
                )}
              </InlineAlert>
            )}

            {withdrawalCancellationRequested ? (
              <InlineAlert type="info" style={{ fontSize: '0.9rem' }}>
                {i18n.gettext(
                  'A cancellation request has already been submitted for this gift certificate.'
                )}
              </InlineAlert>
            ) : isRefundable && hasClaims ? (
              <InlineAlert type="alert" style={{ fontSize: '0.9rem' }}>
                {i18n.gettext(
                  'This gift card has already been (partially) redeemed. Please note that you may be required to reimburse the amount used.'
                )}
              </InlineAlert>
            ) : null}

            <Row>
              <span>{i18n.gettext('Payment')}</span>
              <span>{paymentTypeLabel}</span>
            </Row>
          </DetailsBox>
        </FormColumn>
      </FormRowGroup>

      {isRefundable && !withdrawalCancellationRequested ? (
        <ReasonWrapper>
          <TextField
            aria-label={i18n.gettext(
              'Your reason for cancelling the voucher (optional)'
            )}
            labelStyle={{ ...textFieldLabelStyle, paddingBottom: '0.6rem' }}
            inputStyle={{ resize: 'none' }}
            rows={3}
            multiLine={true}
            visibleMaxLength={256}
            name="withdrawal_reason"
            error={errors.get('withdrawal_reason')}
            placeholder={i18n.gettext('Reason (optional)')}
            value={formValues.get('withdrawal_reason')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFormValue('withdrawal_reason', e.target.value);
              validateField('withdrawal_reason');
            }}
            style={{ width: '100%' }}
          />
        </ReasonWrapper>
      ) : null}
    </div>
  );
});

export default WithdrawalSummary;
